import React, { useState } from "react";
import { auth, db } from "../lib/firebase";
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { toast } from "./UI";

const WHISH_NUMBER = "+96176760820";

export default function SettingsPage({ user, onUpdated }) {
  const [tab,         setTab]         = useState("profile");
  const [name,        setName]        = useState(user.name || "");
  const [phone,       setPhone]       = useState(user.phone || "");
  const [address,     setAddress]     = useState(user.address || "");
  const [savingP,     setSavingP]     = useState(false);
  const [oldPw,       setOldPw]       = useState("");
  const [newPw,       setNewPw]       = useState("");
  const [confirmPw,   setConfirmPw]   = useState("");
  const [savingPw,    setSavingPw]    = useState(false);
  const [contactName, setContactName] = useState(user.name || "");
  const [contactMsg,  setContactMsg]  = useState("");
  const [sendingMsg,  setSendingMsg]  = useState(false);
  const [topupAmt,    setTopupAmt]    = useState("");
  const [showWhish,   setShowWhish]   = useState(false);

  const saveProfile = async () => {
    setSavingP(true);
    try {
      await updateDoc(doc(db, "users", user.uid), { name, phone, address, updatedAt: new Date() });
      toast("Profile updated!");
      if (onUpdated) onUpdated();
    } catch (e) { toast(e.message, "error"); }
    finally { setSavingP(false); }
  };

  const changePassword = async () => {
    if (newPw.length < 6)    { toast("Password must be at least 6 characters.", "error"); return; }
    if (newPw !== confirmPw)  { toast("Passwords do not match.", "error"); return; }
    setSavingPw(true);
    try {
      const cred = EmailAuthProvider.credential(auth.currentUser.email, oldPw);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPw);
      toast("Password changed!");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      if (e.code === "auth/wrong-password") toast("Current password is incorrect.", "error");
      else toast(e.message, "error");
    } finally { setSavingPw(false); }
  };

  const sendContact = async () => {
    if (!contactMsg.trim()) { toast("Please write a message.", "error"); return; }
    setSendingMsg(true);
    try {
      // Using mailto as a fallback — opens email client
      const subject = encodeURIComponent(`LebMarket enquiry from ${contactName}`);
      const body    = encodeURIComponent(`Name: ${contactName}\nEmail: ${user.email}\n\n${contactMsg}`);
      window.open(`mailto:lebmarket@hotmail.com?subject=${subject}&body=${body}`);
      toast("Email client opened!");
      setContactMsg("");
    } catch (e) { toast(e.message, "error"); }
    finally { setSendingMsg(false); }
  };

  const TABS = ["profile", "password", "contact", "top up"];

  return (
    <div className="page-wrap">
      <div className="page-header">
        <h2>Settings</h2>
        <p>Manage your account</p>
      </div>

      <div className="tab-bar">
        {TABS.map(t => (
          <div key={t} className={`tab-item ${tab === t ? "active" : ""}`} onClick={() => setTab(t)} style={{ textTransform: "capitalize" }}>{t}</div>
        ))}
      </div>

      {/* ── Profile ── */}
      {tab === "profile" && (
        <div className="card" style={{ maxWidth: 500 }}>
          <h3 style={{ marginBottom: 16 }}>Profile information</h3>
          <div className="form-row">
            <label>Full name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
          </div>
          <div className="form-row">
            <label>Phone number</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+961 XX XXX XXX" />
          </div>
          <div className="form-row">
            <label>Delivery address (Lebanon)</label>
            <textarea rows={3} value={address} onChange={e => setAddress(e.target.value)} placeholder="Street, building, floor, city..." />
          </div>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>Email address</label>
            <input value={user.email} disabled style={{ opacity: .6, cursor: "not-allowed" }} />
          </div>
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-primary" onClick={saveProfile} disabled={savingP}>
              {savingP ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {/* ── Password ── */}
      {tab === "password" && (
        <div className="card" style={{ maxWidth: 500 }}>
          <h3 style={{ marginBottom: 16 }}>Change password</h3>
          <div className="form-row">
            <label>Current password</label>
            <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="form-row">
            <label>New password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min. 6 characters" />
          </div>
          <div className="form-row">
            <label>Confirm new password</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repeat new password" />
          </div>
          <button className="btn btn-primary" onClick={changePassword} disabled={savingPw}>
            {savingPw ? "Changing..." : "Change password"}
          </button>
        </div>
      )}

      {/* ── Contact ── */}
      {tab === "contact" && (
        <div className="card" style={{ maxWidth: 500 }}>
          <h3 style={{ marginBottom: 4 }}>Contact LebMarket</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>Send us a message and we'll get back to you as soon as possible.</p>
          <div className="form-row">
            <label>Your name</label>
            <input value={contactName} onChange={e => setContactName(e.target.value)} />
          </div>
          <div className="form-row">
            <label>Message</label>
            <textarea rows={5} value={contactMsg} onChange={e => setContactMsg(e.target.value)} placeholder="How can we help you?" />
          </div>
          <button className="btn btn-primary" onClick={sendContact} disabled={sendingMsg}>
            {sendingMsg ? "Opening..." : "Send message"}
          </button>
          <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--bg-soft)", borderRadius: "var(--radius)", fontSize: 13, color: "var(--text-muted)" }}>
            Or reach us directly at <strong>lebmarket@hotmail.com</strong> or via WhatsApp.
          </div>
        </div>
      )}

      {/* ── Top up ── */}
      {tab === "top up" && (
        <div className="card" style={{ maxWidth: 500 }}>
          <h3 style={{ marginBottom: 4 }}>Top up balance</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>Add funds to your account balance via Whish. Your balance will be updated by our team after confirming your payment.</p>

          <div className="form-row">
            <label>Amount to top up ($)</label>
            <input type="number" step="5" min="1" value={topupAmt} onChange={e => setTopupAmt(e.target.value)} placeholder="Enter amount..." />
          </div>

          {!showWhish ? (
            <button className="btn btn-primary btn-full" onClick={() => { if (!topupAmt) { toast("Please enter an amount.", "error"); return; } setShowWhish(true); }}>
              💳 Pay via Whish
            </button>
          ) : (
            <div style={{ background: "var(--green-light)", border: "1.5px solid var(--green-mid)", borderRadius: "var(--radius)", padding: "20px", textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "var(--green-dark)", fontWeight: 600, marginBottom: 10 }}>Send payment via Whish to</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: "var(--green-dark)", marginBottom: 6 }}>{WHISH_NUMBER}</div>
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>${topupAmt}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                Use reference: <strong>TOPUP-LEBMARKET</strong>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>After sending, contact us to confirm. Your balance will be updated within 24 hours.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
